import { ReactElement, useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useParams } from 'react-router-dom'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import RulesList from 'features/Loyalty/components/RulesList/'
import { canRead, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'companies.helmet',
    defaultMessage: 'Loyalty | Campaign | Rules | Nexbus Admin'
  }
})

const GET_CAMPAIGN_RULES = gql`
  query CampaignRules($campaignId: ID!) {
    loyaltyCampaign(input: { id: $campaignId }) {
      id
      rules {
        id
        name
        description
        howToGet
        details
        validAt
        conditions {
          citySegments {
            destination {
              id
            }
            origin {
              id
            }
            segments {
              id
            }
          }
          salesChannels
          totalBookingsValue
        }
        effects {
          reward {
            id
            name
          }
        }
      }
    }
  }
`

const LoyaltyCampaignRules = (): ReactElement => {
  let content = <></>
  const { campaignId } = useParams()
  const intl = useIntl()
  const login = useLogin()

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_CAMPAIGN_RULES, { variables: { campaignId } })

  const showAddNewRuleButton = canWrite(FEATURES.LOYALTY_CAMPAIGN, login.accessToken.policies)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.LOYALTY_CAMPAIGN, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.LOYALTY_CAMPAIGN} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="loyalty.campaign.rules.error.description"
            defaultMessage="There was an error while loading the campaign rules"
          />
        </EmptyState.Description>

        <EmptyState.Description>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="loyalty.campaign.rules.error.action" defaultMessage="Reload campaign rules" />
          </Button>
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.loyaltyCampaign.rules)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="loyalty.campaign.rules.empty.description"
            defaultMessage="You don&#39;t have any rule yet"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to={`/loyalty/campaigns/${campaignId}/rules/new`} kind="primary">
            <FormattedMessage id="loyalty.campaign.rules.empty.action" defaultMessage="Add your first rule" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = <RulesList rules={data.loyaltyCampaign.rules} campaignId={data.loyaltyCampaign.id} />
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="loyalty.campaign.rules.header.label" defaultMessage="Rules" />
        </Heading.H3>
        {showAddNewRuleButton ? (
          <Button as={NavLink} to="new" kind="primary">
            <FormattedMessage id="loyalty.campaign.rules.header.new" defaultMessage="+ Add new rule" />
          </Button>
        ) : null}
      </ContentHeader>
      <ContentBody>{content}</ContentBody>
    </Content>
  )
}

export default withLastUrl(LoyaltyCampaignRules)
